import { motion } from "framer-motion";
import { CheckCircle2, Zap, DollarSign, Wrench, Shield, Home, TrendingUp } from "lucide-react";
import KOPipelineStep from "./KOPipelineStep";
import { adGroups, mockBudget } from "./mockData";

const iconMap: Record<string, React.ReactNode> = {
  Zap: <Zap className="h-5 w-5" />,
  DollarSign: <DollarSign className="h-5 w-5" />,
  Wrench: <Wrench className="h-5 w-5" />,
  Shield: <Shield className="h-5 w-5" />,
  Home: <Home className="h-5 w-5" />,
};

const KOCampaignReady = () => {
  const totalKeywords = adGroups.reduce((sum, g) => sum + g.keywords.length, 0);
  const totalImpressions = adGroups.reduce((sum, g) => sum + g.estImpressions, 0);
  const totalClicks = adGroups.reduce((sum, g) => sum + g.estClicks, 0);
  const avgCPC =
    adGroups.reduce((sum, g) => sum + g.avgCPC * g.keywords.length, 0) / totalKeywords;

  return (
    <section className="py-24 px-4 bg-muted/30">
      <div className="container mx-auto max-w-6xl">
        <KOPipelineStep step={5} title="Your Campaign Is Ready">
          {/* Dashboard preview */}
          <div className="bg-white rounded-2xl border border-border shadow-lg overflow-hidden">
            {/* Dashboard header */}
            <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary/80 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6" />
                <div>
                  <h4 className="font-bold text-lg">Campaign Optimised</h4>
                  <p className="text-sm text-white/80">
                    {totalKeywords} keywords across {adGroups.length} thematic ad groups
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-white/80">Daily budget</p>
                <p className="text-xl font-bold">${mockBudget.dailyBudget}/day</p>
              </div>
            </div>

            {/* Ad group rows */}
            <div className="divide-y divide-border">
              {adGroups.map((group, index) => (
                <motion.div
                  key={group.name}
                  className="px-6 py-4 flex items-center justify-between hover:bg-muted/20 transition-colors"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      {iconMap[group.icon]}
                    </div>
                    <div>
                      <h5 className="font-semibold text-sm">{group.name}</h5>
                      <p className="text-xs text-muted-foreground">
                        {group.keywords.length} keywords
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 text-sm">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Avg CPC</p>
                      <p className="font-semibold text-green-600">${group.avgCPC.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Est. Impressions</p>
                      <p className="font-semibold">{group.estImpressions.toLocaleString()}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-foreground">Est. Clicks</p>
                      <p className="font-semibold">{group.estClicks}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Summary bar */}
            <motion.div
              className="px-6 py-4 bg-primary/5 border-t border-primary/10 flex items-center justify-between"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span className="font-bold text-primary">Campaign Totals</span>
              </div>
              <div className="flex items-center gap-8 text-sm">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Avg CPC</p>
                  <p className="font-bold text-green-600">${avgCPC.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Monthly Impressions</p>
                  <p className="font-bold">{totalImpressions.toLocaleString()}</p>
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-muted-foreground">Monthly Clicks</p>
                  <p className="font-bold">{totalClicks.toLocaleString()}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* SEO text */}
          <motion.p
            className="mt-8 text-muted-foreground leading-relaxed max-w-4xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            The result is a fully structured campaign with five thematic ad groups, each containing
            affordable, high-volume keywords that align with your value proposition. Every keyword
            has been verified against your budget constraints, sorted by potential reach, and grouped
            for maximum ad relevance. This entire process — which would take a Google Ads specialist
            3-4 hours of manual research and analysis — completes automatically in under 5 minutes.
          </motion.p>
        </KOPipelineStep>
      </div>
    </section>
  );
};

export default KOCampaignReady;
